/**
 * Recuperação pré-bootstrap para NG0908 (MISSING_ZONEJS) — GESTOR-CLIENT-PK.
 *
 * Extraído de `src/main.ts` (commit be59e245f) porque nenhuma aplicação de
 * produção usa aquele entrypoint: os builds em `src/build/build-prod.sh`
 * constroem os projetos `gerencie`, `sindicato`, `federacao`, `arquivo`,
 * `convenio`, `eigreja` e `valida-arquivo`, cada um com o seu
 * `src/main/<app>.ts`. A recuperação existia mas nunca chegou ao usuário, e a
 * issue seguiu ocorrendo (último evento em sindicato@14.0.172).
 *
 * Cada entrypoint chama `recoverFromMissingZone()` antes de bootstrapar e
 * aborta o bootstrap se ela devolver true.
 */

/**
 * Guard pré-bootstrap para NG0908 (MISSING_ZONEJS) — Issue GESTOR-CLIENT-PK.
 *
 * Em deploys, o index.html/service worker em cache stale pode servir um
 * `main.[hash].js` sem o `polyfills.[hash].js` correspondente. Sem zone.js
 * carregado, o Angular lança NG0908 no bootstrap e o app trava em tela branca
 * — antes mesmo do PromptUpdateService poder se auto-recuperar.
 *
 * Estratégia: se o global `Zone` estiver ausente, os polyfills não carregaram.
 * Fazemos UMA recuperação controlada (desregistrando o SW desta página e
 * recarregando) para buscar assets frescos da rede.
 *
 * Loop-protection: usamos um contador no query param `_ng0908` da URL — ele
 * sobrevive ao reload sem depender de storage (que pode estar bloqueado em modo
 * privado). Se não for possível gravar o marcador, NÃO recarregamos, para nunca
 * cair em loop infinito.
 */
const RECOVER_PARAM = '_ng0908';

/** Quantas tentativas de recuperação já foram feitas (via query param da URL). */
function getRecoverCount(): number {
  try {
    const raw = new URL(window.location.href).searchParams.get(RECOVER_PARAM);
    const n = parseInt(raw || '0', 10);
    return Number.isNaN(n) ? 0 : n;
  } catch {
    return 0;
  }
}

/** Remove o marcador de recuperação da URL após boot bem-sucedido (URL limpa). */
function clearRecoverParam(): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(RECOVER_PARAM)) {
      url.searchParams.delete(RECOVER_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    /* noop */
  }
}

/**
 * Recarrega marcando a tentativa no query param. Só recarrega se conseguir
 * gravar o marcador — caso contrário retorna false e o caller aborta sem reload.
 */
function reloadWithRecoverMarker(count: number): boolean {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(RECOVER_PARAM, String(count));
    window.location.replace(url.toString());
    return true;
  } catch {
    return false;
  }
}

/**
 * Renderiza um fallback mínimo (DOM puro, sem Angular/Zone) quando a recuperação
 * automática já foi tentada e falhou. Evita deixar o usuário numa tela branca
 * sem ação: oferece um botão que limpa o marcador e recarrega manualmente.
 */
function renderRecoveryFallback(): void {
  try {
    const root = document.body || document.documentElement;
    if (!root) {
      return;
    }
    // Construído com DOM API pura (sem innerHTML) para não depender de uma
    // Trusted Types policy — que não é criada neste caminho de recuperação.
    const box = document.createElement('div');
    box.setAttribute('role', 'alert');
    box.style.cssText =
      'font-family:system-ui,Arial,sans-serif;max-width:420px;margin:15vh auto;padding:24px;' +
      'text-align:center;color:#333;';

    const title = document.createElement('h2');
    title.style.cssText = 'margin:0 0 8px;font-size:18px;';
    title.textContent = 'Não foi possível carregar o aplicativo';

    const msg = document.createElement('p');
    msg.style.cssText = 'margin:0 0 16px;color:#666;';
    msg.textContent = 'Verifique sua conexão e tente novamente.';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.style.cssText =
      'padding:10px 20px;border:0;border-radius:6px;background:#321fdb;color:#fff;' +
      'font-size:14px;cursor:pointer;';
    btn.textContent = 'Recarregar';
    btn.addEventListener('click', () => {
      clearRecoverParam();
      window.location.reload();
    });

    box.appendChild(title);
    box.appendChild(msg);
    box.appendChild(btn);
    root.appendChild(box);
  } catch {
    /* noop */
  }
}

/**
 * @returns true se disparou a recuperação (o caller deve abortar o bootstrap).
 */
export function recoverFromMissingZone(): boolean {
  if (typeof window === 'undefined' || typeof (window as any).Zone !== 'undefined') {
    // Boot normal: limpa o marcador de recuperação da URL, se houver.
    clearRecoverParam();
    return false;
  }

  // zone.js ausente: os polyfills não carregaram.
  if (getRecoverCount() >= 1) {
    // Já tentamos recuperar uma vez e zone.js ainda falta — não entra em loop:
    // mostra fallback com retry manual em vez de tela branca.
    console.error('NG0908: zone.js ausente após recuperação; exibindo fallback.');
    renderRecoveryFallback();
    return true;
  }

  // `info`, não `warn`: o Sentry instala captureConsoleIntegration para
  // 'error' e 'warn' no corpo do CoreModule, que é avaliado no import estático
  // do entrypoint -- ou seja, ANTES desta função rodar. Em nível capturado,
  // uma recuperação bem-sucedida viraria issue e apagaria a diferença entre
  // "recuperou" e "não recuperou". As duas falhas reais abaixo seguem em
  // `error`, e são elas que devem reabrir GESTOR-CLIENT-PK.
  console.info('NG0908: zone.js ausente; recuperando SW e recarregando (tentativa única).');

  const reloadOnce = () => {
    if (!reloadWithRecoverMarker(1)) {
      console.error('NG0908: não foi possível marcar a tentativa na URL; exibindo fallback.');
      renderRecoveryFallback();
    }
  };

  // Desregistra apenas o SW que controla ESTA página. No reload sem SW, os
  // assets vêm frescos da rede (o novo SW recria seus próprios caches). Não
  // deletamos caches manualmente — evita mexer em caches de outros contextos.
  const cleanup =
    'serviceWorker' in navigator
      ? navigator.serviceWorker.getRegistration()
          .then(reg => reg?.unregister())
          .catch(() => undefined)
      : Promise.resolve(undefined);
  cleanup.then(reloadOnce, reloadOnce);
  return true;
}
